import styled from "@emotion/styled/macro";
import {
  Button,
  LoadingIndicator,
  Modal,
  MOBILE_BREAKPOINT,
  SvgIcon,
  SvgNames,
  TABLET_BREAKPOINT,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import {
  ListOffer,
  ReviewOffer,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import SunrunLogo from "components/assets/images/sunrun-logo.svg";
import { DesignRender } from "components/organisms/DesignRender";

type SimplifiedOfferReviewHeaderProps = {
  offer: ReviewOffer;
  address: string;
  utilityCompany?: string;
  customerState?: string;
  isMobile: boolean;
  isOffersLoading: boolean;
  offers: ListOffer[] | undefined;
  cognitoToken: string | undefined;
  isCustomer: boolean;
  onExitClick: () => void;
  navigateToOfferSelector: () => void;
  showMapModalOpen: boolean;
  setShowMapModalOpen: (show: boolean) => void;
  showIntroTagline?: boolean;
};

const SimplifiedOfferReviewHeader = ({
  offer,
  address,
  utilityCompany,
  customerState,
  isMobile,
  isOffersLoading,
  offers,
  cognitoToken,
  isCustomer,
  onExitClick,
  navigateToOfferSelector,
  showMapModalOpen,
  setShowMapModalOpen,
  showIntroTagline = true,
}: SimplifiedOfferReviewHeaderProps) => {
  const showRepAllOffers =
    !isOffersLoading &&
    Boolean(offers?.length) &&
    Boolean(cognitoToken) &&
    !isCustomer;

  const showCustomerAllOffers =
    isCustomer && !isOffersLoading && Boolean(offers?.length);

  const utilityWithState =
    utilityCompany && customerState
      ? `${utilityCompany}(${customerState})`
      : utilityCompany ?? customerState ?? "";

  return (
    <>
      {showMapModalOpen && (
        <SiteModelModal
          onClose={() => setShowMapModalOpen(false)}
          testId="simplified-offer-review-site-model-modal"
        >
          <SiteModelContainer>
            <DesignRender
              designId={offer.designId}
              designVersion={offer.designVersion}
              showTreeRemovalLegend={offer.treeRemovalRequired}
            />
          </SiteModelContainer>
        </SiteModelModal>
      )}
      <HeaderRoot data-testid="simplified-offer-review-header">
        <HeaderInner isMobile={isMobile}>
          <TopRow isMobile={isMobile}>
            <TopLeft>
              {isOffersLoading && <LoadingIndicator />}
              {showRepAllOffers && (
                <AllOffersButton
                  color="secondary"
                  size="sm"
                  onClick={onExitClick}
                  iconLeft={
                    <SvgIcon
                      name={SvgNames.ChevronLeft}
                      width={16}
                      height={16}
                      color={tokens.BRAND_HERO_BLUE}
                    />
                  }
                >
                  All Offers
                </AllOffersButton>
              )}
              {showCustomerAllOffers && (
                <AllOffersButton
                  color="secondary"
                  size="sm"
                  onClick={() => navigateToOfferSelector()}
                  iconLeft={
                    <SvgIcon
                      name={SvgNames.ChevronLeft}
                      width={16}
                      height={16}
                      color={tokens.BRAND_HERO_BLUE}
                    />
                  }
                >
                  All Offers
                </AllOffersButton>
              )}
            </TopLeft>
            <PageTitle
              color={tokens.BRAND_HERO_BLUE}
              isMobile={isMobile}
              align="center"
            >
              System overview
            </PageTitle>
            <LogoImage src={SunrunLogo} alt="Sunrun" />
          </TopRow>
          <FrostedBar isMobile={isMobile}>
            <FrostedBarContent>
              {offer.customerName?.trim() ? (
                <BarCustomerName size={14} color={tokens.BRAND_HERO_BLUE}>
                  {offer.customerName}
                </BarCustomerName>
              ) : null}
              {address ? (
                <BarText size={14} color={tokens.BRAND_HERO_BLUE}>
                  {address}
                </BarText>
              ) : null}
              {utilityWithState ? (
                <BarText size={14} color={tokens.BRAND_HERO_BLUE}>
                  {utilityWithState}
                </BarText>
              ) : null}
            </FrostedBarContent>
          </FrostedBar>
          {showIntroTagline ? (
            <IntroTaglineSection>
              <IntroTagline
                size={32}
                color={tokens.BRAND_HERO_BLUE}
                align="center"
              >
                Take control and back up your home
              </IntroTagline>
            </IntroTaglineSection>
          ) : null}
        </HeaderInner>
      </HeaderRoot>
    </>
  );
};

const HeaderRoot = styled.header`
  width: 100%;
  box-sizing: border-box;
  padding: 24px 0 0;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    padding: 16px 0 0;
  }
`;

const HeaderInner = styled.div<{ isMobile: boolean }>`
  box-sizing: border-box;
  max-width: 1200px;
  width: calc(100% - 128px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? "12px" : "8px")};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: calc(100% - 32px);
  }
`;

const TopRow = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  min-height: 40px;
  gap: 16px;
  margin: 24px 0;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    gap: 12px;
  }
`;

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  justify-self: start;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: 1;
    grid-row: 1;
    justify-self: start;
  }
`;

const AllOffersButton = styled(Button)`
  min-width: 128px;
  height: 39px;
`;

const PageTitle = styled(Typography)<{ isMobile: boolean }>`
  grid-column: 2;
  justify-self: center;
  font-size: 40px !important;
  font-weight: 400 !important;
  line-height: 110% !important;
  letter-spacing: -0.02em;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-self: center;
  }
`;

const LogoImage = styled.img`
  grid-column: 3;
  width: 130px;
  height: auto;
  justify-self: end;
  filter: brightness(0) saturate(100%) invert(11%) sepia(28%) saturate(1450%)
    hue-rotate(198deg) brightness(95%) contrast(95%);

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    margin: auto;
    width: 106px;
    grid-row: 1;
    grid-column: 2;
    justify-self: end;
    align-self: start;
  }
`;

const IntroTaglineSection = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 32px 0;
`;

const IntroTagline = styled(Typography)`
  margin: 0 !important;
  padding: 0 !important;
  font-weight: 600 !important;
  line-height: 110% !important;
  letter-spacing: -0.02em;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    font-size: 28px !important;
  }
`;

const FrostedBar = styled.div<{ isMobile: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 84px;
  padding: ${({ isMobile }) => (isMobile ? "16px" : "20px 24px")};
  border-radius: 10px;
  box-sizing: border-box;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: color-mix(in srgb, ${tokens.WHITE} 30%, transparent);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 0;
  }
`;

const FrostedBarContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: flex-start;
  gap: 24px;
  width: 100%;
`;

const BarCustomerName = styled(Typography)`
  line-height: 140% !important;
  font-weight: 600 !important;
`;

const BarText = styled(Typography)`
  line-height: 140% !important;
  font-weight: 400 !important;
`;

const SiteModelModal = styled(Modal)`
  > .modal-container {
    height: 90%;
    width: 90%;
    max-height: 98vh;
    overflow: hidden;

    > .modal-content-container {
      padding-top: 0px;
      overflow: hidden;
    }

    > #modal-header {
      padding-bottom: 0px;
    }
  }
`;

const SiteModelContainer = styled.div`
  position: relative;
  width: 100%;
  margin: auto;
  padding-top: 10px;
  text-align: center;
`;

export { SimplifiedOfferReviewHeader };
